//@ts-ignore
import React from 'react';
import { graphql } from 'gatsby';
import * as styles from './consultant-testimonial.module.css';
import {
    KontentStringValue,
    BrandColor,
    KontentMedia,
    KontentRadioMultiChoice,
} from '../../../types/KontentProps';
import {
    getColor,
    getMediaData,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';

interface Props {
    consultant_name: KontentStringValue;
    consultant_title?: KontentStringValue;
    customer_testimonial: KontentStringValue;
    customer_name?: KontentStringValue;
    customer_title?: KontentStringValue;
    customer_background_color: BrandColor;
    consultant_background_color: BrandColor;
    consultant_image: KontentMedia;
    consultant_font_color: BrandColor;
    customer_font_color: BrandColor;
    control_bottom_margin?: KontentRadioMultiChoice<string>;
    desktop_bottom_margin?: KontentStringValue;
    mobile_bottom_margin?: KontentStringValue;
    kontentItemId?: string;
}

const ConsultantTestimonial: React.FC<Props> = ({
    consultant_name,
    consultant_title,
    customer_testimonial,
    customer_name,
    customer_title,
    customer_background_color,
    customer_font_color,
    consultant_background_color,
    consultant_font_color,
    consultant_image,
    control_bottom_margin,
    desktop_bottom_margin,
    mobile_bottom_margin,
    kontentItemId,
}) => {
    const consultantImage = getMediaData(consultant_image);
    const url = consultantImage?.url;
    const controlMargin = control_bottom_margin?.value?.[0]?.codename === 'yes';
    const breakpoints = useBreakpoint();
    const marginStyles = controlMargin
        ? {
              marginBottom: breakpoints.md
                  ? `${desktop_bottom_margin?.value || 0}px`
                  : `${mobile_bottom_margin?.value || 0}px`,
          }
        : {};

    return (
        <div
            className={styles.wrapper}
            style={marginStyles}
            data-kontent-item-id={kontentItemId}
        >
            <div
                className={styles.customer}
                style={{
                    background:
                        getColor(customer_background_color) ||
                        'rgb(245, 244, 242)',
                    color: getColor(customer_font_color) || 'rgb(80, 82, 81)',
                }}
            >
                <p
                    style={{
                        alignContent:
                            !customer_name?.value && !customer_title?.value
                                ? 'center'
                                : '',
                        paddingBottom:
                            !customer_name?.value && !customer_title?.value
                                ? '2.5rem'
                                : '',
                    }}
                    data-kontent-element-codename="customer_testimonial"
                >
                    {customer_testimonial.value}
                </p>
                <div style={{ padding: '1.5rem 0' }}>
                    {customer_name?.value && (
                        <p
                            className={styles.customerName}
                            data-kontent-element-codename="customer_name"
                        >
                            &mdash; {customer_name.value}
                        </p>
                    )}
                    {customer_title?.value && (
                        <p
                            className={styles.customerTitle}
                            data-kontent-element-codename="customer_title"
                        >
                            {customer_title.value}
                        </p>
                    )}
                </div>
            </div>
            <div className={styles.consultant}>
                <div data-kontent-element-codename="consultant_image">
                    <LazyImage
                        src={optimizeKontentImageUrl(url, {
                            w: 400,
                            fm: 'webp',
                            q: 80,
                        })}
                        srcSet={buildKontentSrcSet(url, 400, {
                            fm: 'webp' as const,
                            q: 80,
                        })}
                        sizes="200px"
                        alt={
                            consultantImage?.description ||
                            consultantImage?.name ||
                            ''
                        }
                        {...getDisplayDimensions(
                            consultantImage?.width,
                            consultantImage?.height,
                            400
                        )}
                    />
                </div>
                <div
                    className={styles.consultantInfo}
                    style={{
                        background:
                            getColor(consultant_background_color) ||
                            'rgb(245, 244, 242)',
                        color:
                            getColor(consultant_font_color) ||
                            'rgb(80, 82, 81)',
                    }}
                >
                    <h2 data-kontent-element-codename="consultant_name">
                        {consultant_name.value}
                    </h2>
                    {consultant_title && (
                        <p data-kontent-element-codename="consultant_title">
                            {consultant_title.value}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConsultantTestimonial;

export const fragmentConsultantTestimonial = graphql`
    fragment ConsultantTestimonial on kontent_item_component___consultant_testimonial {
        elements {
            consultant_name {
                value
            }
            consultant_title {
                value
            }
            customer_testimonial {
                value
            }
            customer_name {
                value
            }
            customer_title {
                value
            }
            customer_background_color {
                ...kontentColor
            }
            consultant_background_color {
                ...kontentColor
            }
            customer_font_color {
                ...kontentColor
            }
            consultant_font_color {
                ...kontentColor
            }
            consultant_image {
                ...kontentImage
            }
            control_bottom_margin {
                value {
                    name
                    codename
                }
            }
            mobile_bottom_margin {
                value
            }
            desktop_bottom_margin {
                value
            }
        }
    }
`;
